import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import Sheet from "@mui/joy/Sheet";
import axios from "axios";
import { toast } from "react-hot-toast";
import { FiPlus, FiEdit2 } from "react-icons/fi";
import API_BASE_URL from "../../../config/config.js";
import UniformFormModal from "./UniformFormModal.jsx";
import { useUniformOptionsStatus } from "../../../hooks/useUniformOptionsStatus.js";

const getUniformType = (type) => {
    switch (Number(type)) {
        case 1: return "Camiseta";
        case 2: return "Arquero";
        case 3: return "Libero";
        case 4: return "Medias";
        case 5: return "Shorts";
        default: return "Tipo desconocido";
    }
};

const toneLabel = (tone) => (Number(tone) === 1 ? "Claro" : "Oscuro");

const ModalEditUniform = ({ open, setOpen, participant }) => {
    const [loading, setLoading] = useState(false);
    const [uniforms, setUniforms] = useState([]);
    const [formOpen, setFormOpen] = useState(false);
    const [editingUniform, setEditingUniform] = useState(null);
    // FTR-0014: con opciones normalizadas activas (o mientras se consulta) este editor legacy es solo lectura.
    const uniformOptions = useUniformOptionsStatus();
    const readOnly = uniformOptions.loading || uniformOptions.enabled;

    const abrev = participant?.Institution?.abrev || "—";
    const participantId = participant?.idparticipant;

    const fetchUniforms = useCallback(async () => {
        if (!participantId) return;
        setLoading(true);
        try {
            const { data } = await axios.get(`${API_BASE_URL}/uniforms/${participantId}`);
            setUniforms(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            toast.error("No se pudieron cargar los uniformes");
        } finally {
            setLoading(false);
        }
    }, [participantId]);

    useEffect(() => {
        if (open) fetchUniforms();
    }, [open, fetchUniforms]);

    const title = useMemo(() => `Institution: ${abrev}`, [abrev]);

    const onCreateClick = () => {
        setEditingUniform(null);
        setFormOpen(true);
    };

    const onEditClick = (u) => {
        setEditingUniform(u);
        setFormOpen(true);
    };

    const onSaved = async () => {
        setFormOpen(false);
        await fetchUniforms();
        toast.success("Cambios guardados");
    };

    return (
        <>
            <Modal
                aria-labelledby="modal-title"
                aria-describedby="modal-desc"
                open={open}
                onClose={() => setOpen(false)}
                sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
                <Sheet
                    variant="outlined"
                    className="
            w-[94vw] md:w-[920px] max-w-[92vw] md:max-w-[920px]
            rounded-2xl shadow-2xl border border-neutral-200 dark:border-neutral-800
            bg-white dark:bg-neutral-900 overflow-hidden
          "
                >
                    {/* Header */}
                    <div className="px-5 py-4 border-b border-neutral-200 dark:border-neutral-800 bg-gradient-to-r from-neutral-50 to-white dark:from-neutral-900 dark:to-neutral-900">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <h2
                                    id="modal-title"
                                    className="text-xl md:text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50"
                                >
                                    {title}
                                </h2>
                                <p id="modal-desc" className="text-sm text-neutral-500 dark:text-neutral-400">
                                    {readOnly
                                        ? "Solo lectura: las opciones de uniforme se gestionan en Administración General → Uniformes."
                                        : "Gestión de colores y tipos de uniforme."}
                                </p>
                            </div>

                            <div className="flex items-center gap-2">
                                {!readOnly && <button
                                    onClick={onCreateClick}
                                    className="
                    inline-flex items-center gap-2 px-3 py-2 rounded-xl
                    bg-indigo-600 text-white hover:bg-indigo-700 active:scale-[.98]
                    transition shadow-lg shadow-indigo-600/10
                  "
                                >
                                    <FiPlus className="text-base" />
                                    <span className="text-sm font-medium">Nuevo uniforme</span>
                                </button>}

                                <ModalClose
                                    variant="plain"
                                    sx={{ m: 0 }}
                                    className="
                    !m-0 rounded-xl hover:scale-105 transition
                    text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200
                  "
                                />
                            </div>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="px-4 pb-4 pt-3">
                        <div
                            className="
                overflow-auto max-h-[60vh] rounded-xl border border-neutral-200 dark:border-neutral-800
              "
                        >
                            <table className="min-w-full text-sm">
                                <thead
                                    className="
                    sticky top-0 z-10
                    bg-neutral-100/80 dark:bg-neutral-800/80
                    backdrop-blur supports-[backdrop-filter]:backdrop-blur
                    border-b border-neutral-200 dark:border-neutral-800
                  "
                                >
                                    <tr className="text-left text-neutral-700 dark:text-neutral-300">
                                        <th className="px-4 py-3 w-12">#</th>
                                        <th className="px-4 py-3">Color</th>
                                        <th className="px-4 py-3">Type</th>
                                        <th className="px-4 py-3">Tone</th>
                                        <th className="px-4 py-3">Code</th>
                                        <th className="px-4 py-3 w-24">Preview</th>
                                        <th className="px-4 py-3 w-20">Acciones</th>
                                    </tr>
                                </thead>

                                <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                                    {loading && (
                                        <tr>
                                            <td colSpan={7} className="px-4 py-6">
                                                <div className="animate-pulse text-neutral-500">Cargando uniformes…</div>
                                            </td>
                                        </tr>
                                    )}

                                    {!loading && uniforms.length === 0 && (
                                        <tr>
                                            <td colSpan={7} className="px-4 py-6">
                                                <div className="flex items-center justify-between">
                                                    <div className="text-neutral-500">Aún no hay uniformes.</div>
                                                    {!readOnly && <button
                                                        onClick={onCreateClick}
                                                        className="
                              inline-flex items-center gap-2 px-3 py-2 rounded-xl
                              border border-dashed border-neutral-300 dark:border-neutral-700
                              hover:bg-neutral-50 dark:hover:bg-neutral-800 transition
                            "
                                                    >
                                                        <FiPlus />
                                                        <span className="text-sm">Añadir el primero</span>
                                                    </button>}
                                                </div>
                                            </td>
                                        </tr>
                                    )}

                                    {!loading &&
                                        uniforms.map((row, idx) => {
                                            const hex = row.code ? `#${row.code}` : null;
                                            const isLight = Number(row.tone) === 1;
                                            return (
                                                <tr
                                                    key={row.iduniform ?? `${row.idparticipant}-${idx}`}
                                                    className={`
                            ${idx % 2 === 0 ? "bg-white/60 dark:bg-neutral-900/60" : "bg-neutral-50/60 dark:bg-neutral-950/40"}
                            hover:bg-neutral-100/80 dark:hover:bg-neutral-800/60 transition-colors
                          `}
                                                >
                                                    <td className="px-4 py-3 font-medium text-neutral-600 dark:text-neutral-300">
                                                        {row.iduniform ?? idx + 1}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <span
                                                                className="inline-block h-3 w-3 rounded-full ring-1 ring-neutral-300 dark:ring-neutral-700"
                                                                style={{ backgroundColor: hex || "transparent" }}
                                                                title={hex || "sin code"}
                                                            />
                                                            <span className="text-neutral-800 dark:text-neutral-200">
                                                                {row.color || "—"}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-neutral-700 dark:text-neutral-300">
                                                        {getUniformType(row.type)}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span
                                                            className={`
                                inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
                                ${isLight
                                                                    ? "bg-amber-100 text-amber-800 ring-1 ring-amber-200"
                                                                    : "bg-slate-200 text-slate-800 ring-1 ring-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:ring-slate-600"}
                              `}
                                                        >
                                                            {toneLabel(row.tone)}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 font-mono text-[13px] text-neutral-700 dark:text-neutral-300">
                                                        {row.code || "—"}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div
                                                            className="h-6 w-16 rounded-md ring-1 ring-neutral-300 dark:ring-neutral-700"
                                                            style={{ backgroundColor: hex || "transparent" }}
                                                            title={hex || "sin code"}
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        {!readOnly && <button
                                                            className="
                                inline-flex items-center gap-1 px-2 py-1 rounded-lg
                                border border-neutral-300 dark:border-neutral-700
                                hover:bg-neutral-100 dark:hover:bg-neutral-800 transition text-sm
                              "
                                                            onClick={() => onEditClick(row)}
                                                            title="Editar"
                                                        >
                                                            <FiEdit2 />
                                                            Editar
                                                        </button>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer */}
                        <div className="mt-4 flex items-center justify-end gap-2">
                            <button
                                onClick={() => setOpen(false)}
                                className="
                  px-4 py-2 rounded-xl border border-neutral-300 dark:border-neutral-700
                  text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800
                  transition
                "
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </Sheet>
            </Modal>

            {/* Modal de formulario (crear/editar) */}
            <UniformFormModal
                open={formOpen && !readOnly}
                onClose={() => setFormOpen(false)}
                onSaved={onSaved}
                participantId={participantId}
                initialUniform={editingUniform}
            />
        </>
    );
};

export default ModalEditUniform;
